import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { Column, ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import BankSecurityRow from '~/components/BankSecurityRow/BankSecurityRow';
import { AddPaymentMethodOptions } from '~/components/ManagePaymentMethods/AddPaymentMethodOptions';
import BankAccountPanel from '~/components/ManagePaymentMethods/BankAccountPanel';
import { ConnectToAgreementsSelect } from '~/components/ManagePaymentMethods/ConnectToAgreementsSelect/ConnectToAgreementsSelect';
import CreditCardPanel from '~/components/ManagePaymentMethods/CreditCardPanel';
import { PaymentMethodPanelSkeleton } from '~/components/ManagePaymentMethods/PaymentMethodPanel';
import { useAddPaymentMethodFlow } from '~/components/ManagePaymentMethods/useAddPaymentMethodFlow';
import { useStore } from '~/contexts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useLocaleKeys } from '~/i18n';
import { paymentMethodModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useClientAgreementsForConnect } from '../hooks/useClientAgreementsForConnect';
import { PartialConnectError, useConnectFundingSourceToAgreements } from '../hooks/useConnectFundingSourceToAgreements';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
export const GetClientDetailsPaymentMethodsTID = (parentTestId)=>getTestIds(parentTestId);
const READ_ONLY_SUMMARY_PANEL_PROPS = {
    readOnly: true,
    isSelected: false,
    selectable: false,
    onSelect: noop
};
function PaymentMethodSummary(param) {
    let { paymentMethod, clientId, vendorId, apiGatewayHost } = param;
    if (paymentMethod == null) return /*#__PURE__*/ _jsx(PaymentMethodPanelSkeleton, {});
    return paymentMethodModel.isBankAccount(paymentMethod) ? /*#__PURE__*/ _jsx(BankAccountPanel, _object_spread_props(_object_spread({}, READ_ONLY_SUMMARY_PANEL_PROPS), {
        bankAccount: paymentMethod,
        isProposal: false,
        isPaymentMethodInMotion: false,
        verifyable: false,
        customerId: clientId,
        vendorId: vendorId,
        apiGatewayHost: apiGatewayHost,
        onSuccess: noop,
        onVerify: noop
    })) : /*#__PURE__*/ _jsx(CreditCardPanel, _object_spread_props(_object_spread({}, READ_ONLY_SUMMARY_PANEL_PROPS), {
        creditCard: paymentMethod
    }));
}
export function ClientDetailsPaymentMethods(param) {
    let { clientId, dismissModal, testId } = param;
    var _useStore;
    const { ClientDetailsPaymentMethodsTestIds } = useMemo(()=>GetClientDetailsPaymentMethodsTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys('pages.clientDetail.summary.paymentMethods');
    const { t: managePaymentMethodsTexts } = useLocaleKeys('components.managePaymentMethods');
    const queryClient = useQueryClient();
    const overlayContext = useOverlayContext();
    const vendorId = (_useStore = useStore(selectors.user.businessId)) !== null && _useStore !== void 0 ? _useStore : '';
    const apiGatewayHost = useStore((state)=>state.session.apiGatewayHost);
    const [step, setStep] = useState('add');
    const [createdFundingSourceId, setCreatedFundingSourceId] = useState(null);
    const [selectedAgreementIds, setSelectedAgreementIds] = useState([]);
    const [showValidationError, setShowValidationError] = useState(false);
    const { agreements, isLoading, isError } = useClientAgreementsForConnect(clientId, {
        refreshOnHookMount: true
    });
    const { paymentMethods, refresh } = usePaymentMethods(clientId, {
        refreshOnHookMount: true
    });
    const refreshPaymentMethods = useCallback(()=>refresh({
            throwOnError: false
        }).then((result)=>{
            var _result_data;
            return {
                paymentMethods: (_result_data = result.data) !== null && _result_data !== void 0 ? _result_data : []
            };
        }), [
        refresh
    ]);
    const invalidatePaymentMethods = useCallback(()=>queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.CustomerPaymentMethods,
                vendorId,
                clientId
            ]
        }), [
        queryClient,
        vendorId,
        clientId
    ]);
    const { connect } = useConnectFundingSourceToAgreements({
        agreements,
        onSuccess: (connectedAgreementCount)=>{
            invalidatePaymentMethods();
            overlayContext.showSuccessToast(connectedAgreementCount > 0 ? texts.connectSuccess() : texts.addSuccess(), ClientDetailsPaymentMethodsTestIds.SuccessToast());
            dismissModal();
        }
    });
    const onFundingSourceCreated = useCallback(async (fundingSourceId)=>{
        setCreatedFundingSourceId(fundingSourceId);
        setStep('connect');
        await refreshPaymentMethods();
    }, [
        refreshPaymentMethods
    ]);
    const flow = useAddPaymentMethodFlow({
        mode: 'modal',
        vendorId,
        customerId: clientId,
        paymentMethods,
        refresh: refreshPaymentMethods,
        onFundingSourceCreated
    });
    const onSave = async ()=>{
        if (selectedAgreementIds.length === 0) {
            setShowValidationError(true);
            return;
        }
        setShowValidationError(false);
        try {
            await connect({
                fundingSourceId: createdFundingSourceId !== null && createdFundingSourceId !== void 0 ? createdFundingSourceId : '',
                selectedAgreementIds
            });
        } catch (error) {
            if (error instanceof PartialConnectError) {
                overlayContext.showErrorToast(texts.connectPartialFailure({
                    agreements: error.failedAgreementNames.join(', ')
                }), ClientDetailsPaymentMethodsTestIds.ErrorToast());
            }
        }
    };
    const onSkip = ()=>{
        invalidatePaymentMethods();
        overlayContext.showSuccessToast(texts.addSuccess(), ClientDetailsPaymentMethodsTestIds.SuccessToast());
        dismissModal();
    };
    const createdPaymentMethod = paymentMethods.find((paymentMethod)=>paymentMethod.id === createdFundingSourceId);
    const isConnectStep = step === 'connect';
    const addStepContent = /*#__PURE__*/ _jsxs(Column, {
        gap: 24,
        children: [
            /*#__PURE__*/ _jsx(AddPaymentMethodOptions, {
                testId: ClientDetailsPaymentMethodsTestIds.Root(),
                flow: flow,
                vendorId: vendorId,
                customerId: clientId,
                creditCardFeeLabel: managePaymentMethodsTexts.creditCardFeeByClient(),
                achFeeLabel: managePaymentMethodsTexts.noFee()
            }),
            /*#__PURE__*/ _jsx(BankSecurityRow, {
                "data-testid": ClientDetailsPaymentMethodsTestIds.BankSecurity()
            })
        ]
    });
    const connectStepContent = /*#__PURE__*/ _jsxs(Column, {
        gap: 24,
        children: [
            /*#__PURE__*/ _jsx(PaymentMethodSummary, {
                paymentMethod: createdPaymentMethod,
                clientId: clientId,
                vendorId: vendorId,
                apiGatewayHost: apiGatewayHost
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph1",
                skin: "secondary",
                testId: ClientDetailsPaymentMethodsTestIds.ConnectStepSubtitle(),
                children: texts.connectStepSubtitle()
            }),
            /*#__PURE__*/ _jsx(ConnectToAgreementsSelect, {
                agreements: agreements,
                isLoading: isLoading,
                isError: isError,
                onSelectionApplied: setSelectedAgreementIds
            }),
            showValidationError && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                skin: "error",
                testId: ClientDetailsPaymentMethodsTestIds.SelectAgreementValidation(),
                children: texts.selectAgreementValidation()
            })
        ]
    });
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        testId: ClientDetailsPaymentMethodsTestIds.Root(),
        skin: "transparent",
        useStandardTextColors: true,
        textAlignCenter: false,
        width: "760px",
        title: isConnectStep ? texts.connectStepTitle() : texts.modalTitle(),
        onCloseButtonClick: dismissModal,
        bi: {
            closeButton: {
                name: 'client_detail_add_payment_method_close'
            },
            primaryButton: {
                name: 'connect_payment_method_save'
            },
            secondaryButton: {
                name: 'skip_connect_agreements'
            }
        },
        primaryButtonLabel: isConnectStep ? texts.save() : undefined,
        primaryButtonTestId: ClientDetailsPaymentMethodsTestIds.SaveButton(),
        onPrimaryButtonClick: onSave,
        secondaryButtonLabel: isConnectStep ? texts.skipForNow() : undefined,
        secondaryButtonTestId: ClientDetailsPaymentMethodsTestIds.SkipButton(),
        onSecondaryButtonClick: onSkip,
        content: isConnectStep ? connectStepContent : addStepContent
    });
}
